import Decimal from 'decimal.js';
import { getProperty } from 'dot-prop';
import { isEmpty, isNil, pick, upperFirst } from 'lodash-es';
import type { Primitive } from 'type-fest';
import { z } from 'zod';

import { safeDecimal } from 'utils/decimalUtils';
import { selectOptionDataSchema } from 'utils/selectOptionDataSchema';
import { decimalPercentToString } from 'utils/stringUtils';

import type {
  Location,
  LocationInput,
  MonetaryAmount,
  MonetaryAmountFragment,
  PriceRetailBulkAdjustmentAttributes,
} from 'store/client/graphql';
import { MileageUnit } from 'store/client/graphql';

import { type Intl, isFrCA, Locale, t, translate } from './intlUtils';

export const PLACEHOLDER = '-';

interface FormatCurrencyProps {
  /**
   * Currency value to be formatted
   */
  amount: Decimal;
  /**
   * Currency to use in formatting (default is CAD)
   */
  currency?: 'CAD' | 'USD';
  /**
   * Locale to use in formatting (default is EN_CA
   */
  locale?: Locale;
  /**
   * Whether to omit the dollar sign
   */
  omitDollarSign?: boolean;
}

export const formatCurrency = ({
  amount,
  currency = 'CAD',
  locale = Locale.EN_CA,
  omitDollarSign = false,
}: FormatCurrencyProps) => {
  const formatter = new Intl.NumberFormat(locale, {
    currency,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'currency',
  });

  const formattedValue = formatter.format(amount.toString() as `${number}`);

  return omitDollarSign ? formattedValue.replace('$', '') : formattedValue;
};

const numberFormatter = new Intl.NumberFormat();
export const formatNumber = (number: number) => numberFormatter.format(number);

type User = { displayName?: null | string; firstName?: null | string; lastName?: null | string };
export const formatFullName = (user?: User, fallback?: string) =>
  user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || fallback || '';

export const formatInitials = (user: User, separator = '') =>
  user.firstName || user.lastName
    ? formatFullName(user)
        .split(' ')
        .map(word => word?.[0] || '')
        .reduce((accumulator: string[], value) => {
          if (accumulator.length > 0) {
            accumulator[1] = value;
          } else {
            accumulator[0] = value;
          }

          return accumulator;
        }, [])
        .join(separator)
        .toUpperCase()
    : '?';

export const formatAmount = (monetaryAmount: null | Pick<MonetaryAmountFragment, 'formattedAmount'> | undefined) =>
  getProperty(monetaryAmount, 'formattedAmount', '');

/**
 * Whole-unit form of an amount, for places that would otherwise repeat `.00` cents as visual noise.
 * The API pre-formats it, so this never rounds locally.
 */
export const formatAmountRounded = (
  monetaryAmount: null | Pick<MonetaryAmount, 'formattedAmountRounded'> | undefined
) => getProperty(monetaryAmount, 'formattedAmountRounded', '');

export const formatBoolean = (
  bool: boolean,
  yesNoOverride?: { noTranslationKey: Intl; yesTranslationKey: Intl }
): string => translate.t(bool ? yesNoOverride?.yesTranslationKey || 'yes' : yesNoOverride?.noTranslationKey || 'no');

export const formatLocationData = (data?: Location | LocationInput, inputsOnly = false) => {
  if (isEmpty(data)) {
    return;
  }
  const locationInputKeys: Array<keyof LocationInput> = [
    'address',
    'city',
    'countryCode',
    'latitude',
    'longitude',
    'placeId',
    'regionCode',
    'zipCode',
  ];

  const filteredObject = inputsOnly ? pick(data, locationInputKeys) : { ...data };

  for (const key of Object.keys(filteredObject)) {
    filteredObject[key] = filteredObject[key]?.hasOwnProperty('value')
      ? filteredObject[key]?.value
      : filteredObject[key];
  }

  return filteredObject;
};

type Coordinate = Decimal | null | number | string | undefined;

/**
 * Formats a latitude/longitude pair as a parenthesized suffix for a location's address line,
 * e.g. ` (43.637840, -79.392450)`. Returns an empty string unless both coordinates are present
 * and valid, so callers can append it unconditionally; each value is normalized through
 * `safeDecimal` and fixed to 6 decimals.
 */
export const formatLocationCoordinates = (latitude: Coordinate, longitude: Coordinate): string => {
  if (latitude == null || longitude == null) {
    return '';
  }
  const formattedLatitude = Decimal.isDecimal(latitude) ? latitude : safeDecimal(latitude, { allowInfinite: false });
  const formattedLongitude = Decimal.isDecimal(longitude)
    ? longitude
    : safeDecimal(longitude, { allowInfinite: false });

  return formattedLatitude && formattedLongitude
    ? ` (${formattedLatitude.toFixed(6)}, ${formattedLongitude.toFixed(6)})`
    : '';
};

const colorDataSchema = selectOptionDataSchema(z.object({ rgbHex: z.string() }), 'Error reading colour metadata');

export const formatColorFromData = (data?: any, fallback?: string): string =>
  colorDataSchema.safeParse(data).data?.rgbHex || fallback || '';

export const formatFileSize = (bytes: number): string => {
  const KB = 1024;
  const MB = KB * KB;
  const GB = MB * KB;

  if (bytes >= GB) {
    return `${(bytes / GB).toFixed(2)} GB`;
  }
  if (bytes >= MB) {
    return `${(bytes / MB).toFixed(2)} MB`;
  }
  if (bytes >= KB) {
    return `${(bytes / KB).toFixed(2)} KB`;
  }

  return `${bytes} B`;
};

// Helper that returns true/false depending on  whether the value is withing the java 32int supported value
export const is32IntCompatible = (value: number | string): boolean => Number(value) <= 2_147_483_647;

/**
 * Helper that returns true/false depending on whether the BigDecimal value is within Java's double precision limits
 * Java's Double.MAX_VALUE is approximately 1.7976931348623157e+308
 */
export const isBigDecimalCompatible = (value: Decimal): boolean => {
  const maxValue = new Decimal('1.7976931348623157e+308');
  const minValue = new Decimal('-1.7976931348623157e+308');

  return value.gte(minValue) && value.lte(maxValue) && value.isFinite();
};

/**
 * Formats a long block of text to an ellipsed version, if needed.
 * Used for preview purposes. e.g. in ListItems.
 * @param {string} fullText The raw text content
 * @param {number} maxTextLength The max characters shown before it is ellipsed.
 */
export const formatTextForPreview = (fullText: null | string | undefined, maxTextLength: number) => {
  if (!fullText) {
    return fullText;
  }
  const sanitizedInput = fullText.replaceAll(/(\r\n|\n|\r)/gm, ' ');

  return sanitizedInput?.length > maxTextLength
    ? `${sanitizedInput?.slice(0, Math.max(0, maxTextLength))}...`
    : sanitizedInput;
};

export const formatTextWithBullets = (text: Array<null | string | undefined>) => {
  const filtered = text.filter(Boolean);

  return filtered.length > 0 ? filtered.join('  •  ') : null;
};

/**
 * Canonical sign convention for adjustment amounts across the app: an amount encodes the "negative"
 * direction — discount for retail price adjustments, reduction for trade-in valuation adjustments —
 * when it is strictly less than zero. Zero and positive values are the positive direction (fee /
 * increase). Accepts both plain numbers (builder step state) and Decimal-like API values (anything
 * exposing `lessThan`) so display and create/modify paths share one rule instead of hand-rolling it.
 * A nil amount (a row with neither `fixed` nor `percentage` set) is treated as the positive direction,
 * matching the zero convention and avoiding a throw when direction is inferred from unset amounts.
 * @param value - the signed amount, as a number or a Decimal-like value (or nil for an unset amount)
 */
export const isNegativeSign = (value: null | number | Pick<Decimal, 'lessThan'> | undefined): boolean => {
  if (isNil(value)) {
    return false;
  }

  return typeof value === 'number' ? value < 0 : value.lessThan(0);
};

type SignedAdjustmentAmount = {
  /**
   * Fixed monetary amount; a negative amount denotes the negative direction (discount).
   */
  fixed?: null | { amount: number | Pick<Decimal, 'lessThan'> };
  /**
   * Percentage amount used when no fixed amount is set; a negative value denotes the negative direction.
   */
  percentage?: null | number | Pick<Decimal, 'lessThan'>;
};

export const isPriceAdjustmentDiscount = (adjustment: SignedAdjustmentAmount): boolean =>
  isNil(adjustment.fixed)
    ? !isNil(adjustment.percentage) && isNegativeSign(adjustment.percentage)
    : isNegativeSign(adjustment.fixed.amount);

export const formatPriceAdjustmentAmount = (adjustment?: PriceRetailBulkAdjustmentAttributes): null | string => {
  if (isNil(adjustment)) {
    return null;
  }

  const label = t(isPriceAdjustmentDiscount(adjustment) ? 'discount' : 'fee');

  if (isNil(adjustment.fixed) && isNil(adjustment.percentage)) {
    return label;
  }

  const value = isNil(adjustment.fixed)
    ? decimalPercentToString(adjustment.percentage as Decimal, 2)
    : adjustment.fixed.formattedAmount;

  return `${label}: ${value}`;
};

/**
 * Format range data, rules are as follows:
 * If only gte value is defined, string will be '≥ gte'
 * If only lte value is defined, string will be '≤ lte'
 * If both gte and lte are defined, string will be 'gte - lte'
 * @param range - A range data type that contains a lte and gte property
 * @param units - Optional units to display
 */
export const formatRange = (range: { gte?: any; lte?: any }, units?: string): string => {
  const formatUnits = units ? ` ${units}` : '';

  if (range?.gte && range.lte) {
    return `${range?.gte}${formatUnits} - ${range?.lte}${formatUnits}`;
  }
  if (range?.gte) {
    return `≥ ${range?.gte}${formatUnits}`;
  }
  if (range?.lte) {
    return `≤ ${range?.lte}${formatUnits}`;
  }

  return PLACEHOLDER;
};

export const formatMileage = (amount: number | string, unit: MileageUnit): string => {
  const mileageAmount = Number(amount);

  return `${Number.isNaN(mileageAmount) ? 0 : mileageAmount.toLocaleString()} ${
    unit === MileageUnit.KILOMETERS ? 'km' : 'mi'
  }`;
};

export const formatTitleString = (str: string): string =>
  isFrCA()
    ? upperFirst(str.toLowerCase())
    : str
        .split(/\s/g)
        .map(word => upperFirst(word))
        .join(' ');

export const isPrimitive = (value: unknown): value is Primitive => {
  const typeOfValue = typeof value;

  return (
    value === null ||
    typeOfValue === 'undefined' ||
    typeOfValue === 'boolean' ||
    typeOfValue === 'number' ||
    typeOfValue === 'string' ||
    typeOfValue === 'bigint' ||
    typeOfValue === 'symbol'
  );
};
